import { useTranslation } from 'next-i18next';

import { Box, Heading, HStack, Separator, Text } from '@chakra-ui/react';

import { dateHelpers } from '@nocowanie/core';
import { Tooltip } from '@nocowanie/common-ui';

import { ForeignPrice } from '@app/components/atoms/foreign-price';
import { ReservationPrice } from '@app/components/molecules/reservation-price';
import { DATE_FORMAT } from '@app/consts';

import { ReservationPriceBoxProps } from './reservation-price-box.props';

export const ReservationPriceBox = ({
    withPrepayment,
    currency,
    prepayment,
    prepaymentDate,
    totalPrice,
    convertedRatesCurrency,
    basePrice,
    convertedBasePrice,
    convertedTotalPrice,
    remainingPrice,
    withOnlinePayment,
    guests,
    childrenAges,
    nightsCount,
    isAvantioAccommodation,
    ...rest
}: ReservationPriceBoxProps) => {
    const { t } = useTranslation('checkout');

    return (
        <Box p={4} backgroundColor={'green.50'} borderRadius={'md'} {...rest}>
            <Heading as={'h4'} size={'xs'} mb={4}>
                {t('paymentSection.priceDetails')}
            </Heading>
            <Box>
                <HStack justify={'space-between'} gap={4} mb={2}>
                    <HStack
                        gap={0}
                        fontSize={{
                            base: 'sm',
                            lg: 'md',
                        }}
                    >
                        {isAvantioAccommodation ? (
                            <>
                                <Text as={'span'}>{t('paymentSection.prepayment.label')}</Text>
                                <Tooltip positioning={{ placement: 'top' }}>
                                    {t('paymentSection.prepayment.bankTransferDetails')}
                                </Tooltip>
                            </>
                        ) : (
                            <>
                                <Text as={'span'}>
                                    {withPrepayment
                                        ? `${t(
                                              'paymentSection.prepayment.label',
                                          )} (do ${dateHelpers.format(
                                              new Date(prepaymentDate ?? ''),
                                              DATE_FORMAT.displayedDateYearFormatWithNameOfMonth,
                                          )})`
                                        : t('paymentSection.fullPayment.label')}
                                </Text>
                                {withPrepayment ? (
                                    <Tooltip positioning={{ placement: 'top' }}>
                                        {withOnlinePayment
                                            ? t('paymentSection.prepayment.onlinePaymentDetails')
                                            : t('paymentSection.prepayment.bankTransferDetails')}
                                    </Tooltip>
                                ) : null}
                            </>
                        )}
                    </HStack>
                    <ForeignPrice
                        price={
                            withPrepayment && !isAvantioAccommodation
                                ? (prepayment?.amount ?? 0)
                                : (totalPrice ?? 0)
                        }
                        currency={currency}
                        convertedPrice={
                            withPrepayment && !isAvantioAccommodation
                                ? (prepayment?.convertedAmount ?? undefined)
                                : (convertedTotalPrice ?? undefined)
                        }
                        convertedCurrency={convertedRatesCurrency ?? undefined}
                        fontWeight={'normal'}
                        fontSize={{ base: 'sm', md: 'md' }}
                    />
                </HStack>
                {!isAvantioAccommodation && remainingPrice ? (
                    <>
                        <HStack
                            justify={'space-between'}
                            gap={4}
                            fontSize={{ base: 'sm', md: 'md' }}
                        >
                            <Text>{t('paymentSection.prepayment.remainingPrice')}</Text>
                            {remainingPrice}
                        </HStack>
                    </>
                ) : null}
            </Box>
            <Separator my={4} />
            <HStack justify={'space-between'} align={'start'}>
                <Text fontSize={{ base: 'sm', md: 'md' }} fontWeight={'bold'}>
                    {t('paymentSection.fullPayment.fullPrice')}
                </Text>
                <Box textAlign={'right'}>
                    <ReservationPrice
                        currency={currency ?? ''}
                        totalPrice={totalPrice ?? 0}
                        basePrice={basePrice ?? 0}
                        convertedRatesCurrency={convertedRatesCurrency}
                        convertedTotalPrice={convertedTotalPrice}
                        convertedBasePrice={convertedBasePrice}
                        guests={guests ?? 0}
                        childrenAges={childrenAges}
                        nightsCount={nightsCount}
                    />
                    <Box as={'span'} fontSize={'sm'} color={'gray'} mt={2}>
                        {t('paymentSection.priceGuarantee')}
                    </Box>
                </Box>
            </HStack>
        </Box>
    );
};
